import { type MotionValue, motion, useSpring, useTransform } from 'motion/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import type { CounterProps } from './Counter.types';

interface NumberProps {
  mv: MotionValue<number>;
  digit: number;
  height: number;
}

function DigitNumber({ mv, digit, height }: NumberProps) {
  const y = useTransform(mv, (latest) => {
    const placeValue = latest % 10;
    const offset = (10 + digit - placeValue) % 10;
    let memo = offset * height;
    if (offset > 5) {
      memo -= 10 * height;
    }
    return memo;
  });

  return (
    <motion.span className="absolute inset-0 flex items-center justify-center" style={{ y }}>
      {digit}
    </motion.span>
  );
}

interface DigitProps {
  place: number;
  value: number;
  height: number;
}

function Digit({ place, value, height }: DigitProps) {
  const valueRoundedToPlace = Math.floor(value / place);
  const animatedValue = useSpring(valueRoundedToPlace);

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace]);

  return (
    <div
      className="relative w-[1ch] shrink-0 overflow-hidden tabular-nums before:content-['0'] before:invisible"
      style={{ height }}
    >
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
        <DigitNumber key={`digit-${digit}`} mv={animatedValue} digit={digit} height={height} />
      ))}
    </div>
  );
}

/**
 * 숫자의 자릿수에 맞는 places 배열 생성
 * @example getAutoPlaces(1234) => [1000, 100, 10, 1]
 */
function getAutoPlaces(value: number, minDigits = 1): number[] {
  const absValue = Math.abs(value);
  const digits = absValue === 0 ? 1 : Math.floor(Math.log10(absValue)) + 1;
  const actualDigits = Math.max(digits, minDigits);
  return Array.from({ length: actualDigits }, (_, i) => 10 ** (actualDigits - 1 - i));
}

/**
 * Counter 컴포넌트 - 숫자 애니메이션
 *
 * @example
 * // 기본 사용 - 부모 스타일 상속
 * <span className="text-[36px] text-white font-bold">
 *   <Counter value={1234} />
 * </span>
 *
 * // 최소 자릿수 지정
 * <Counter value={5} minDigits={3} /> // 005로 표시
 */
const Counter = ({ value, minDigits = 1, className }: CounterProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<number>(0);

  // 부모로부터 font-size 상속받기
  useLayoutEffect(() => {
    if (containerRef.current) {
      const computed = window.getComputedStyle(containerRef.current);
      setFontSize(parseFloat(computed.fontSize));
    }
  }, []);

  // 자릿수 자동 계산
  const places = getAutoPlaces(value, minDigits);

  // fontSize가 계산되기 전에는 숨김
  if (fontSize === 0) {
    return (
      <span ref={containerRef} className={cn('inline', className)}>
        {value}
      </span>
    );
  }

  return (
    <span ref={containerRef} className={cn('block w-full max-w-full', className)}>
      <span className="flex w-full max-w-full flex-wrap overflow-visible leading-none">
        {places.map((place) => (
          <Digit key={place} place={place} value={value} height={fontSize} />
        ))}
      </span>
    </span>
  );
};

export default Counter;
